import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';

interface BookCastRowProps {
  bookId: number;
  bookTitle: string;
}

interface BookAvatar {
  id: string;
  image_url: string;
  display_order: number;
}

export function BookCastRow({ bookId, bookTitle }: BookCastRowProps) {
  const { data: avatars, isLoading } = useQuery({
    queryKey: ['book-avatars', bookId],
    queryFn: async () => {
      // First try to get avatars by book_id
      let { data, error } = await supabase
        .from('book_avatars')
        .select('id, image_url, display_order')
        .eq('book_id', bookId)
        .order('display_order', { ascending: true });

      // If no avatars found by book_id, try by book_title
      if (!error && (!data || data.length === 0)) {
        const titleResult = await supabase
          .from('book_avatars')
          .select('id, image_url, display_order')
          .eq('book_title', bookTitle)
          .order('display_order', { ascending: true });
        
        if (!titleResult.error) {
          data = titleResult.data;
        }
      }

      if (error) throw error;
      return data as BookAvatar[];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  if (isLoading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex-shrink-0">
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/10 animate-pulse" />
          </div>
        ))}
      </div>
    );
  }

  if (!avatars || avatars.length === 0) {
    return null;
  }

  return (
    <div className="py-4">
      <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-3">
        Cast
      </h3>
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
        {avatars.map((avatar, index) => (
          <motion.div
            key={avatar.id}
            className="flex-shrink-0"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05, duration: 0.3 }}
          >
            <div className="w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden bg-white/5">
              <img
                src={avatar.image_url}
                alt="Cast member"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
